import { resolveRelatedVariant } from './related-product-options.js';
import { RelatedProductSectionCache } from './related-product-section-cache.js';
import { RelatedProductEffects } from './related-product-effects.js';

class RelatedProductPDP extends HTMLElement {
  connectedCallback() {
    this.cache = new RelatedProductSectionCache();
    this.effects = new RelatedProductEffects(this);
    this.prefetchRequests = new Map();
    this.onPageLoaded = () => { this.pageLoaded = true; this.schedulePrefetch(); };
    this.pageLoaded = document.readyState === 'complete';
    if (!this.pageLoaded) window.addEventListener('load', this.onPageLoaded, { once: true });
    this.sectionId = this.dataset.sectionId;
    this.content = this.querySelector('[data-related-product-content]');
    this.status = this.querySelector('[data-related-product-status]');
    this.fallback = this.querySelector('[data-related-product-fallback]');
    this.onClick = this.handleClick.bind(this);
    this.onSubmit = this.handleSubmit.bind(this);
    this.onPopState = this.handlePopState.bind(this);
    this.onVariantChange = this.handleVariantChange.bind(this);
    this.onCartAdded = this.handleCartAdded.bind(this);
    this.onReady = this.initializeContent.bind(this);
    this.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit, true);
    window.addEventListener('popstate', this.onPopState);
    window.addEventListener('stickyProductCTA:ready', this.onReady);
    window.eventBus.on('variant:change', this.onVariantChange);
    window.eventBus.on('cart:added', this.onCartAdded);
    this.committedURL = window.location.href;
    this.readPageData();
    this.initializeContent();
    this.trackProductView();
    this.schedulePrefetch();
  }

  disconnectedCallback() {
    this.request?.abort();
    this.effects.cancel();
    window.removeEventListener('load', this.onPageLoaded);
    clearTimeout(this.prefetchTimer);
    if (this.prefetchIdle != null) window.cancelIdleCallback?.(this.prefetchIdle);
    this.prefetchRequests.forEach((request) => request.abort());
    this.cache.entries.clear();
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('submit', this.onSubmit, true);
    window.removeEventListener('popstate', this.onPopState);
    window.removeEventListener('stickyProductCTA:ready', this.onReady);
    window.eventBus.off('variant:change', this.onVariantChange);
    window.eventBus.off('cart:added', this.onCartAdded);
    window.destroyStickyProductCTAVisibility?.(this.querySelector('.sticky-product-cta'));
  }

  readPageData() {
    this.pageData = JSON.parse(this.content.querySelector('[data-related-product-page-data]').textContent);
  }

  initializeContent() {
    const sticky = this.querySelector('.sticky-product-cta');
    const initializers = [
      () => this.updateSetAvailability(),
      () => sticky && window.initStickyProductCTAVisibility?.({
        stickySelector: `.sticky-product-cta[data-sticky-cta-section-id="${this.sectionId}"]`,
        triggerSelector: `product-information[data-section-id="${this.sectionId}"]`,
        alwaysVisible: sticky.dataset.relatedAlwaysVisible === 'true'
      }),
      () => sticky && window.initStickyProductCTAPriceSync?.(sticky),
      () => window.Shopify?.PaymentButton?.init(),
      () => window.yotpoWidgetsContainer?.initWidgets?.(),
      () => window.yotpo?.initWidgets?.(),
      () => window.changeTab?.(this.querySelector('.tab-names li')?.dataset.tab)
    ];
    for (const initialize of initializers) {
      try { initialize(); } catch (error) { console.warn('Product widget initialization failed', error); }
    }
    try {
      const previous = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]');
      localStorage.setItem('theme:recently-viewed-products', JSON.stringify([this.pageData.id, ...previous.filter((id) => id !== this.pageData.id)].slice(0, 20)));
    } catch {}
  }

  updateSetAvailability() {
    const selector = this.querySelector('.related-product-selector');
    if (!selector) return;
    const targets = JSON.parse(selector.querySelector('[data-related-product-targets]').textContent);
    const selectedOptions = this.selectedOptions;
    const information = this.querySelector('product-information');
    const selectedId = information.currentVariant?.id || information.querySelector('[name="id"]')?.value;
    selector.querySelectorAll('a[data-related-product-link]').forEach((link) => {
      const target = targets.find((item) => String(item.id) === link.dataset.relatedProductId);
      if (!target) return;
      const variant = link.getAttribute('aria-current') === 'page'
        ? target.variants.find((item) => String(item.id) === String(selectedId))
        : resolveRelatedVariant(target.variants, selectedOptions);
      const available = variant?.available === true;
      link.classList.toggle('is-soldout', !available);
      const status = link.querySelector('[data-set-sold-out]');
      if (status) status.hidden = available;
      if (variant) {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('variant', variant.id);
        link.href = url.href;
      }
    });
  }

  handleSubmit(event) {
    if (!this.hasAttribute('data-loading') && !this.stockUnverified) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  handleClick(event) {
    const link = event.target.closest('a[data-related-product-link]');
    if (!link || !this.contains(link) || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey || link.target === '_blank') return;
    const targetURL = new URL(link.href, window.location.origin);
    if (targetURL.origin !== window.location.origin) return;
    event.preventDefault();
    if (link.closest('.related-product-selector').dataset.scrollToTopMobile !== 'false' && window.matchMedia?.('(max-width: 767px)').matches) {
      window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
    if (link.getAttribute('aria-current') === 'page' && !this.stockUnverified) return;
    if (link.hasAttribute('data-preserve-options')) {
      const targets = JSON.parse(link.closest('.related-product-selector').querySelector('[data-related-product-targets]').textContent);
      const target = targets.find((item) => String(item.id) === link.dataset.relatedProductId);
      if (!target) return;
      const variant = resolveRelatedVariant(target.variants, this.selectedOptions);
      if (!variant) return;
      targetURL.searchParams.set('variant', variant.id);
    }
    this.navigate(targetURL, { history: 'push', focus: true });
  }

  get selectedOptions() {
    const information = this.querySelector('product-information');
    const data = JSON.parse(information.querySelector('[data-all-variants]').textContent);
    const selectedId = information.currentVariant?.id || information.querySelector('[name="id"]')?.value;
    return data.variants.find((variant) => String(variant.id) === String(selectedId))?.options || {};
  }

  setBusy(busy) {
    this.toggleAttribute('data-loading', busy);
    this.content.setAttribute('aria-busy', String(busy));
    this.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout').forEach((element) => { element.inert = busy || this.stockUnverified; });
    if (busy) {
      this.buttonStates ||= new Map();
      this.querySelectorAll('button[type="submit"]').forEach((button) => {
        if (!this.buttonStates.has(button)) this.buttonStates.set(button, button.disabled);
        button.disabled = true;
      });
      this.status.textContent = this.dataset.loadingText;
      this.status.hidden = false;
    } else {
      this.buttonStates?.forEach((disabled, button) => {
        if (button.isConnected) button.disabled = disabled;
      });
      this.buttonStates = null;
    }
  }

  makeURL(input) {
    const url = new URL(input, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('Invalid product origin');
    const current = new URL(window.location.href);
    for (const key of ['preview_theme_id', '_fd', 'pb', 'view']) {
      if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
    }
    url.searchParams.delete('sections');
    url.searchParams.delete('section_id');
    return url;
  }

  getSectionElements() {
    const main = this.closest('[id^="shopify-section-"]');
    const siblings = Array.from(document.querySelectorAll('#MainContent > [id^="shopify-section-"]'));
    return [main, ...siblings.filter((section) => section !== main && section.dataset.relatedProductStatic !== 'true')];
  }

  async fetchSections(url, elements, signal) {
    const result = new Map();
    for (let index = 0; index < elements.length; index += 5) {
      const batch = elements.slice(index, index + 5);
      const requestURL = new URL(url);
      requestURL.searchParams.set('sections', batch.map((element) => element.id.replace('shopify-section-', '')).join(','));
      const response = await fetch(requestURL, { signal, cache: 'no-store' });
      if (!response.ok) throw new Error('Product request failed');
      const data = await response.json();
      for (const element of batch) {
        const id = element.id.replace('shopify-section-', '');
        if (typeof data[id] !== 'string') throw new Error('Missing product section');
        const parsed = new DOMParser().parseFromString(data[id], 'text/html');
        const next = parsed.getElementById(element.id);
        if (!next) throw new Error('Invalid section response');
        result.set(element, next);
      }
    }
    return result;
  }

  schedulePrefetch() {
    if (!this.pageLoaded || !this.isConnected || window.Shopify?.designMode) return;
    clearTimeout(this.prefetchTimer);
    if (this.prefetchIdle != null) window.cancelIdleCallback?.(this.prefetchIdle);
    this.prefetchTimer = setTimeout(() => {
      if (window.requestIdleCallback) this.prefetchIdle = window.requestIdleCallback(() => this.prefetchProducts(), { timeout: 2000 });
      else this.prefetchProducts();
    }, 1000);
  }

  async prefetchProducts() {
    if (!this.isConnected || this.request) return;
    if (this.prefetchRunning) { this.prefetchAgain = true; return; }
    const links = this.querySelectorAll('a[data-related-product-link]');
    if (!links.length) return;
    const urls = [...new Set([...links].filter((link) => new URL(link.href).origin === window.location.origin).map((link) => this.makeURL(link.href).href))];
    this.prefetchRunning = true;
    this.cache.limit = Math.max(this.cache.limit, urls.length + 8);
    const workers = Array.from({ length: 2 }, async () => {
      while (urls.length && this.isConnected && !this.request) {
        const url = urls.shift();
        const key = this.cache.key(url);
        if (this.cache.get(url) || this.prefetchRequests.has(key)) continue;
        const request = new AbortController();
        const revision = ++this.cache.revision;
        const timer = setTimeout(() => request.abort(), 15000);
        this.prefetchRequests.set(key, request);
        try {
          const elements = this.getSectionElements();
          const sections = await this.fetchSections(new URL(url), elements, request.signal);
          const snapshot = this.cache.capture(sections, revision);
          this.validateSections(sections, new URL(url));
          await this.cache.warmImages(snapshot, elements[0].id);
          if (this.isConnected && !request.signal.aborted) {
            this.cache.put(url, snapshot);
            if (!this.visibleSnapshot && key === this.cache.key(this.makeURL(this.committedURL))) this.visibleSnapshot = snapshot;
          }
        } catch {} finally {
          clearTimeout(timer);
          if (this.prefetchRequests.get(key) === request) this.prefetchRequests.delete(key);
        }
      }
    });
    await Promise.all(workers);
    this.prefetchRunning = false;
    if (this.prefetchAgain) { this.prefetchAgain = false; this.schedulePrefetch(); }
  }

  validateSections(sections, url) {
    const main = sections.get(this.closest('[id^="shopify-section-"]'));
    const nextContent = main?.querySelector('[data-related-product-content]');
    const metadata = nextContent?.querySelector('[data-related-product-page-data]');
    if (!nextContent?.querySelector('product-information') || !metadata) throw new Error('Incompatible product template');
    const data = JSON.parse(metadata.textContent);
    if (new URL(data.url, window.location.origin).pathname !== url.pathname) throw new Error('Unexpected product');
    if (url.searchParams.has('variant') && String(data.variantId) !== url.searchParams.get('variant')) throw new Error('Unexpected product variant');
    return { nextContent, data };
  }

  updateSectionLifecycle(section, unload = false) {
    const theme = window.wetheme;
    if (!theme) return;
    const elements = [...section.querySelectorAll('[data-wetheme-section-type]')];
    if (section.matches('[data-wetheme-section-type]')) elements.unshift(section);
    for (const element of elements) {
      const { wethemeSectionId: sectionId, wethemeSectionType: type } = element.dataset;
      if (!theme.SECTIONS?.[type] || (unload && !theme.sectionById?.has(sectionId))) continue;
      try {
        const event = { target: element, detail: { sectionId } };
        if (unload) theme.onSectionUnload?.(event);
        else theme.onSectionLoad?.(event);
      } catch (error) { console.warn('Product section initialization failed', error); }
    }
  }

  applySections(sections, url, { baseline, snapshot, focus = false, history = 'none', variant, notify = true } = {}) {
    const { nextContent, data } = this.validateSections(sections, url);
    const main = this.closest('[id^="shopify-section-"]');
    const incomingSnapshot = snapshot || this.cache.capture(sections);
    baseline ||= this.visibleSnapshot;
    const changed = (element) => !baseline || baseline.sections.get(element.id)?.signature !== incomingSnapshot.sections.get(element.id)?.signature;
    const previousProductId = this.pageData.id;
    const mainChanged = changed(main);
    this.isReplacing = true;
    if (mainChanged) {
      const sameVariant = this.pageData.id === data.id && this.pageData.variantId === data.variantId;
      const draft = sameVariant ? this.captureDraft() : [];
      const focusedSet = this.querySelector('.related-product-selector__option.is-current') === document.activeElement;
      window.destroyStickyProductCTAVisibility?.(this.querySelector('.sticky-product-cta'));
      this.setAttribute('data-instant-switch', '');
      nextContent.querySelectorAll('[data-product-media-wrapper]').forEach((element) => element.setAttribute('data-media-settled', ''));
      nextContent.querySelectorAll('.show-on-scroll, [data-animate], [data-animate-nested], .kb-enabled').forEach((element) => {
        element.classList.remove('show-on-scroll', 'kb-enabled');
        element.removeAttribute('data-animate');
        element.removeAttribute('data-animate-nested');
      });
      this.content.replaceChildren(...nextContent.childNodes);
      this.readPageData();
      this.restoreDraft(draft);
      if (focusedSet) this.querySelector('.related-product-selector__option.is-current')?.focus({ preventScroll: true });
    }
    let lowerSectionsChanged = false;
    for (const [previous, next] of sections) {
      if (previous === main || !changed(previous)) continue;
      this.updateSectionLifecycle(previous, true);
      previous.replaceWith(next);
      this.updateSectionLifecycle(next);
      lowerSectionsChanged = true;
    }
    if (mainChanged || lowerSectionsChanged) {
      this.initializeContent();
      window.wetheme?.showOnScroll?.();
    }
    this.visibleSnapshot = incomingSnapshot;
    this.isReplacing = false;
    if (variant === null) this.disableInvalidSelection();
    if (!url.searchParams.has('option_values')) url.searchParams.set('variant', this.pageData.variantId);
    this.commitURL(url, history);
    this.syncMetadata();
    if (previousProductId !== this.pageData.id) this.trackProductView();
    if (notify) {
      window.eventBus.emit('variant:updated', { sectionId: this.sectionId, variant: variant ?? null });
      window.dispatchEvent(new CustomEvent('related-product:changed', { detail: { product: this.pageData, url: url.href } }));
    }
    if (focus) this.querySelector('.related-product-selector__option.is-current, .product-details-product-title')?.focus({ preventScroll: true });
  }

  showSections(sections, url, options) {
    const { data } = this.validateSections(sections, url);
    if (data.id === this.pageData.id) {
      this.applySections(sections, url, options);
      return;
    }
    const request = this.request;
    return this.effects.run(() => {
      if (this.isConnected && this.request === request) this.applySections(sections, url, options);
    });
  }

  captureDraft() {
    return [...this.querySelectorAll('input[name="quantity"], [name^="properties["]')].map((element) => ({
      form: element.form?.id, name: element.name, value: element.value, checked: element.checked, type: element.type, focused: element === document.activeElement
    }));
  }

  restoreDraft(draft) {
    const elements = [...this.querySelectorAll('input[name="quantity"], [name^="properties["]')];
    draft.forEach((state) => {
      const element = elements.find((item) => item.name === state.name && item.form?.id === state.form && item.type === state.type && (!['radio', 'checkbox'].includes(item.type) || item.value === state.value));
      if (!element || element.type === 'file') return;
      element.value = state.value;
      if (['radio', 'checkbox'].includes(element.type)) element.checked = state.checked;
      if (state.focused) element.focus({ preventScroll: true });
    });
  }

  async navigate(input, { history = 'push', focus = false, variant } = {}) {
    const url = this.makeURL(input);
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    this.prefetchRequests.get(this.cache.key(url))?.abort();
    const revision = ++this.cache.revision;
    const cached = variant === null ? null : this.cache.get(url);
    this.unverifiedButtonStates?.forEach((disabled, button) => { if (button.isConnected) button.disabled = disabled; });
    this.unverifiedButtonStates = null;
    const previousUnverified = this.stockUnverified;
    this.stockUnverified = false;
    this.fallback.hidden = true;
    this.status.classList.remove('is-error');
    const timer = setTimeout(() => request.abort(new Error('Product request timed out')), 15000);
    let shownCached = false;
    try {
      let cachedSections;
      if (cached) {
        try {
          cachedSections = this.cache.parse(cached, this.getSectionElements());
          this.validateSections(cachedSections, url);
        } catch {
          cachedSections = null;
          this.cache.entries.delete(this.cache.key(url));
        }
      }
      if (cachedSections) {
        this.setBusy(true);
        const transition = this.showSections(cachedSections, url, { history, focus, variant, snapshot: cached });
        if (transition) await transition;
        if (this.request !== request || !this.isConnected) return;
        shownCached = true;
      }
      this.setBusy(true);
      const sections = await this.fetchSections(url, this.getSectionElements(), request.signal);
      if (this.request !== request || !this.isConnected) return;
      this.validateSections(sections, url);
      const snapshot = this.cache.capture(sections, revision);
      const transition = this.showSections(sections, url, { baseline: shownCached ? cached : null, history: shownCached ? 'none' : history, focus: !shownCached && focus, variant, notify: !shownCached, snapshot });
      if (transition) await transition;
      if (this.request !== request || !this.isConnected) return;
      this.cache.put(url, snapshot);
      this.status.textContent = this.dataset.changedText;
    } catch (error) {
      if (this.request !== request || !this.isConnected) return;
      this.stockUnverified = shownCached || previousUnverified;
      if (!shownCached && history === 'none') this.commitURL(this.committedURL, 'replace');
      this.status.classList.add('is-error');
      this.status.textContent = this.dataset.errorText;
      this.fallback.href = url.href;
      this.fallback.hidden = false;
    } finally {
      clearTimeout(timer);
      if (this.request === request && this.isConnected) {
        this.isReplacing = false;
        this.request = null;
        this.setBusy(false);
        if (variant === null) this.disableInvalidSelection();
        if (this.stockUnverified) {
          this.unverifiedButtonStates = new Map();
          this.querySelectorAll('button[type="submit"]').forEach((button) => {
            this.unverifiedButtonStates.set(button, button.disabled);
            button.disabled = true;
          });
        }
        this.schedulePrefetch();
      }
    }
  }

  commitURL(input, mode) {
    const url = this.makeURL(input);
    if (mode === 'push' && url.href !== this.committedURL) window.history.pushState({}, '', url);
    if (mode === 'replace') window.history.replaceState(window.history.state, '', url);
    this.committedURL = url.href;
  }

  handlePopState() {
    this.navigate(window.location.href, { history: 'none' });
  }

  handleVariantChange(context) {
    if (this.isReplacing || context.sectionId !== this.sectionId) return;
    if (context.variant?.force_out_of_stock && String(context.variant.id) === String(this.pageData.variantId)) return;
    let url = context.fetchURL;
    if (!url) {
      const information = this.querySelector('product-information');
      const values = Array.from(information.querySelectorAll('li[data-product-variant][selected], input[data-product-variant]:checked')).map((input) => input.dataset.optionValueId);
      const target = this.makeURL(this.pageData.url);
      target.searchParams.set('option_values', values.join(','));
      url = target.href;
    }
    this.navigate(url, { history: 'replace', variant: context.variant });
  }

  disableInvalidSelection() {
    this.querySelectorAll('button[type="submit"], input[name="id"]').forEach((element) => { element.disabled = true; });
    this.querySelectorAll('[data-add-to-cart-text]').forEach((element) => { element.textContent = element.dataset.unavailableText; });
  }

  syncMetadata() {
    document.title = this.pageData.documentTitle;
    const canonical = new URL(this.pageData.url, window.location.origin).href;
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonical);
    const tags = { 'description': this.pageData.description, 'og:title': this.pageData.title, 'og:description': this.pageData.description, 'og:url': canonical, 'og:image': this.pageData.image, 'og:image:secure_url': this.pageData.image, 'og:price:amount': this.pageData.price, 'og:price:currency': this.pageData.currency, 'twitter:title': this.pageData.title, 'twitter:description': this.pageData.description, 'twitter:image': this.pageData.image };
    for (const [name, value] of Object.entries(tags)) {
      document.querySelectorAll(`meta[name="${name}"], meta[property="${name}"]`).forEach((tag) => tag.setAttribute('content', value ?? ''));
    }
  }

  trackProductView() {
    const item = this.pageData.tracking;
    const queue = window._learnq = window._learnq || [];
    queue.push(['track', 'Viewed Product', item]);
    queue.push(['track', 'Viewed Item', { Title: item.Name, ItemId: item.ProductID, Categories: item.Categories, ImageURL: item.ImageURL, Url: item.URL, Metadata: { Brand: item.Brand, Price: item.Price, CompareAtPrice: item.CompareAtPrice } }]);
  }

  handleCartAdded(context) {
    if (context.sectionId !== this.sectionId || context.success !== true || context.productURL !== this.pageData.url) return;
    (window._learnq = window._learnq || []).push(['track', 'Added to Cart', this.pageData.tracking]);
  }
}

if (!customElements.get('related-product-pdp')) customElements.define('related-product-pdp', RelatedProductPDP);
